import { usePrevious } from '@sequencehq/utils'
import { useSequenceInfiniteQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useNotifications } from '@sequencehq/core-components'
import { useEffect, useMemo } from 'react'
import { useAtomValue } from 'jotai'
import { adminViewEnabledAtom } from '../manualTasksInbox/atoms'
import {
  watchtowerFiltersAtom,
  mapTriggerToReviewType,
  NOTIFICATIONS_FILTER_VALUE
} from 'Automations/watchtower/atoms'
import { getNotificationTaskTypes } from 'Automations/watchtower/inbox/isRecognisedTaskType'
import { match } from 'ts-pattern'
import { usePermissions } from 'lib/rbac'

const DEFAULT_POLLING_INTERVAL = 5000
export const useAdminManualTasks = (
  props: {
    enabled?: boolean
    pollingInterval?: number
    type?: string
    status?: string
  } = {}
) => {
  const { displayNotification } = useNotifications()
  const manualTasksInfiniteQuery = useSequenceInfiniteQuery(
    dashboardv99990101Client.getAdminManualTasks,
    {
      status: props.status,
      type: props.type,
      limit: 100
    },
    {
      retry: false,
      refetchInterval: props.pollingInterval ?? DEFAULT_POLLING_INTERVAL,
      enabled: props.enabled ?? true
    }
  )

  const prevErrorState = usePrevious(
    manualTasksInfiniteQuery.infiniteQuery.isError
  )

  useEffect(() => {
    if (manualTasksInfiniteQuery.infiniteQuery.isError && !prevErrorState) {
      displayNotification(
        'We could not load the admin view of Watchtower tasks',
        {
          type: 'error'
        }
      )
    }
  }, [
    prevErrorState,
    manualTasksInfiniteQuery.infiniteQuery.isError,
    displayNotification
  ])

  const allManualTasks = useMemo(
    () =>
      manualTasksInfiniteQuery.infiniteQuery.data?.pages.flatMap(
        page => page.items
      ) ?? [],
    [manualTasksInfiniteQuery.infiniteQuery]
  )

  return {
    isPending: manualTasksInfiniteQuery.infiniteQuery.isPending,
    isError: manualTasksInfiniteQuery.infiniteQuery.isError,
    isFetchingNextPage:
      manualTasksInfiniteQuery.infiniteQuery.isFetchingNextPage,
    hasNextPage: manualTasksInfiniteQuery.infiniteQuery.hasNextPage,
    fetchNextPage: manualTasksInfiniteQuery.infiniteQuery.fetchNextPage,
    manualTasks: allManualTasks,
    totalPendingTasks:
      manualTasksInfiniteQuery.infiniteQuery.data?.pages[0].pagination
        .totalResultSize
  }
}

export const useAssignedManualTasks = (
  props: {
    pollingInterval?: number
    enabled?: boolean
    type?: string
    status?: string
  } = {}
) => {
  const { displayNotification } = useNotifications()

  const manualTasksInfiniteQuery = useSequenceInfiniteQuery(
    dashboardv99990101Client.getManualTasks,
    {
      status: props.status,
      type: props.type,
      limit: 100
    },
    {
      refetchInterval: props.pollingInterval ?? DEFAULT_POLLING_INTERVAL,
      retry: false,
      enabled: props.enabled ?? true
    }
  )

  const prevErrorState = usePrevious(
    manualTasksInfiniteQuery.infiniteQuery.isError
  )

  useEffect(() => {
    if (manualTasksInfiniteQuery.infiniteQuery.isError && !prevErrorState) {
      displayNotification('We could not load your Watchtower inbox', {
        type: 'error'
      })
    }
  }, [
    prevErrorState,
    manualTasksInfiniteQuery.infiniteQuery.isError,
    displayNotification
  ])

  const allManualTasks = useMemo(() => {
    return (
      manualTasksInfiniteQuery.infiniteQuery.data?.pages.flatMap(
        page => page.items
      ) ?? []
    )
  }, [manualTasksInfiniteQuery.infiniteQuery])

  return {
    isPending: manualTasksInfiniteQuery.infiniteQuery.isPending,
    isError: manualTasksInfiniteQuery.infiniteQuery.isError,
    isFetchingNextPage:
      manualTasksInfiniteQuery.infiniteQuery.isFetchingNextPage,
    hasNextPage: manualTasksInfiniteQuery.infiniteQuery.hasNextPage,
    fetchNextPage: manualTasksInfiniteQuery.infiniteQuery.fetchNextPage,
    manualTasks: allManualTasks,
    totalPendingTasks:
      manualTasksInfiniteQuery.infiniteQuery.data?.pages[0].pagination
        .totalResultSize
  }
}

/**
 * Returns the list of manual tasks for the user.
 * If admin view is enabled, returns all manual tasks.
 * Otherwise, returns only the manual tasks assigned to the user.
 *
 * @param props
 * @returns
 */
export const useManualTasks = (
  props: { pollingInterval?: number; enabled?: boolean; status?: string } = {}
) => {
  const adminViewEnabled = useAtomValue(adminViewEnabledAtom)
  const filters = useAtomValue(watchtowerFiltersAtom)
  const { isAdmin } = usePermissions()

  const reviewTypes = mapTriggerToReviewType(filters.trigger)
  const notificationTypes = filters.trigger.includes(NOTIFICATIONS_FILTER_VALUE)
    ? getNotificationTaskTypes()
    : []
  const allTypes = [...reviewTypes, ...notificationTypes]
  const typeFilter = allTypes.length > 0 ? allTypes.join(',') : undefined

  const isEnabled = props.enabled ?? true

  const {
    manualTasks: adminManualTasks,
    isPending: isAdminManualTasksPending,
    isError: isAdminManualTasksError,
    isFetchingNextPage: isAdminFetchingNextPage,
    hasNextPage: adminHasNextPage,
    fetchNextPage: adminFetchNextPage
  } = useAdminManualTasks({
    enabled: isEnabled && adminViewEnabled && isAdmin,
    type: typeFilter,
    status: props.status
  })
  const {
    isPending: isAssignedManualTasksPending,
    isError: isAssignedManualTasksError,
    isFetchingNextPage: isAssignedFetchingNextPage,
    manualTasks: assignedManualTasks,
    hasNextPage: assignedHasNextPage,
    fetchNextPage: assignedFetchNextPage
  } = useAssignedManualTasks({
    enabled: isEnabled && !(adminViewEnabled && isAdmin),
    pollingInterval: props.pollingInterval,
    type: typeFilter,
    status: props.status
  })

  return match(adminViewEnabled)
    .with(true, () => ({
      isPending: isAdminManualTasksPending,
      isError: isAdminManualTasksError,
      isFetchingNextPage: isAdminFetchingNextPage,
      hasNextPage: adminHasNextPage,
      fetchNextPage: adminFetchNextPage,
      manualTasks: adminManualTasks,
      totalPendingTasks: adminManualTasks.length
    }))
    .otherwise(() => ({
      isPending: isAssignedManualTasksPending,
      isError: isAssignedManualTasksError,
      isFetchingNextPage: isAssignedFetchingNextPage,
      hasNextPage: assignedHasNextPage,
      fetchNextPage: assignedFetchNextPage,
      manualTasks: assignedManualTasks,
      totalPendingTasks: assignedManualTasks.length
    }))
}
